/**
 * Accessibility patches for third-party app widgets and cross-app conflicts.
 *
 * Vendor apps inject their own markup after page load, so these fixes must run
 * client-side. Each patch is defensive: if the vendor changes its markup the
 * patch silently no-ops. Review after app updates (Corner, Smile, Loox).
 */
(function () {
  "use strict";

  /**
   * Corner (cowi) widget renders its close control as a clickable <div>.
   * Give it button semantics and keyboard operability.
   */
  function patchCornerCloseButton(root) {
    var btn = (root || document).querySelector("#corner-cowi-close-btn:not([role])");
    if (!btn) return;
    btn.setAttribute("role", "button");
    btn.setAttribute("tabindex", "0");
    if (!btn.hasAttribute("aria-label")) {
      btn.setAttribute("aria-label", "Close");
    }
    btn.addEventListener("keydown", function (event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        btn.click();
      }
    });
  }

  /**
   * The Corner cart app removes/replaces the theme cart drawer, leaving
   * aria-controls attributes pointing at ids that no longer exist. Strip any
   * aria-controls whose target is absent so the reference is never invalid.
   * Skips elements inside shadow roots (theme components manage their own).
   */
  function removeStaleAriaControls() {
    document.querySelectorAll("[aria-controls]").forEach(function (el) {
      var id = el.getAttribute("aria-controls");
      if (id && !document.getElementById(id)) {
        el.removeAttribute("aria-controls");
      }
    });
  }

  /**
   * Persistent live region announcing cart changes to screen readers.
   * Cart mutations come from the theme AND third-party widgets (Corner,
   * si-widget) that call /cart/*.js directly and never dispatch the theme's
   * cart:change event — so watch the network layer rather than theme events.
   */
  function setupCartAnnouncer() {
    var region = document.createElement("div");
    region.className = "sr-only";
    region.setAttribute("role", "status");
    region.setAttribute("aria-live", "polite");
    document.body.appendChild(region);

    var timer = null;
    function announce() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        fetch("/cart.js")
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            var count = cart.item_count;
            region.textContent = "Cart updated: " + count + (count === 1 ? " item" : " items");
          })
          .catch(function () {
            region.textContent = "Cart updated";
          });
      }, 300);
    }

    var CART_URL = /\/cart\/(add|change|update|clear)(\.js|\.json)?/;

    var origFetch = window.fetch;
    window.fetch = function () {
      var arg = arguments[0];
      var url = typeof arg === "string" ? arg : (arg && arg.url) || "";
      var result = origFetch.apply(this, arguments);
      if (CART_URL.test(url)) {
        result.then(function (res) { if (res.ok) announce(); }, function () {});
      }
      return result;
    };

    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (typeof url === "string" && CART_URL.test(url)) {
        this.addEventListener("load", function () {
          if (this.status >= 200 && this.status < 300) announce();
        });
      }
      return origOpen.apply(this, arguments);
    };
  }

  /**
   * Shopify's video_tag filter emits a fallback <img> inside <video> with no
   * alt attribute and no Liquid hook to set one. Mark them decorative.
   */
  function patchVideoPosterImages() {
    document.querySelectorAll("video img:not([alt])").forEach(function (img) {
      img.setAttribute("alt", "");
    });
  }

  function runPatches() {
    patchCornerCloseButton(document);
    removeStaleAriaControls();
    patchVideoPosterImages();
  }

  function init() {
    setupCartAnnouncer();
    runPatches();
    // Vendor widgets mount late; re-run when new nodes land, debounced.
    var timer = null;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(runPatches, 500);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
